"use client";
import Link from "next/link";
import { PortableText, PortableTextReactComponents } from "next-sanity";
import { Button } from "../ui/button";
import { iconsMap } from "../icons";
import { getURL } from "@/lib/utils";
import { HeroSectionType } from "./hero";
import { StatCard } from "../shared/stat-card";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AspectRatio } from "../ui/aspect-ratio";
import { SanityImage } from "@/components/sanity-image";

export function SolutionsHero({ data }: { data: HeroSectionType }) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { title, subtitle, description, ctas, mainImage, statCards } = data;

  const components: Partial<PortableTextReactComponents> = {
    block: {
      h1: ({ children }) => (
        <h1 className="text-4xl xl:text-5xl 2xl:text-6xl font-semibold text-[#1A1A1A] leading-[120%]">
          {children}
        </h1>
      ),
      normal: ({ children }) => (
        <p className="text-[#808080] font-light leading-relaxed md:max-w-[450px] lg:max-w-xl">
          {children}
        </p>
      ),
    },
  };

  return (
    <section className="space-y-8 bg-[#D2ECFC1F] relative">
      <div className="mx-auto grid md:grid-cols-2 items-center gap-12 font-outfit max-w-screen-2xl px-6 md:px-8 md:h-[calc(100vh-84px)] md:max-h-[769px] lg:max-h-[min(calc(100vh-84px),900px)] xl:max-h-[calc(100vh-84px)] justify-center w-full">
        <div className="space-y-6 pt-20 md:py-0">
          {title && <PortableText value={title} components={components} />}
          {subtitle && <PortableText value={subtitle} components={components} />}
          {description && <PortableText value={description} components={components} />}

          <div className="flex gap-4 flex-wrap">
            {ctas?.map((cta, i) => {
              const link = getURL(cta);
              return (
                <Button
                  key={i}
                  variant={cta.variant || "default"}
                  radius="full"
                  size={isMobile ? "lg" : "xl"}
                  asChild
                >
                  <Link
                    href={link}
                    target={cta.openInNewTab ? "_blank" : "_self"}
                    rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                  >
                    {cta.textBeforeIcon}
                    {cta.icon && iconsMap[cta.icon]}
                    {cta.textAfterIcon}
                  </Link>
                </Button>
              );
            })}
          </div>
        </div>
        <div className="w-full relative hidden md:block">
          <AspectRatio ratio={565 / 500}>
            {mainImage && mainImage.asset && (
              <SanityImage
                src={mainImage}
                alt={mainImage.alt || ""}
                fill
                placeholder="blur"
                blurDataURL={mainImage.asset?.metadata?.lqip || ""}
                className="object-cover rounded-t-2xl md:rounded-2xl lg:rounded-b-2xl cursor-pointer"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            )}
          </AspectRatio>
          {statCards?.map((card) => (
            <StatCard
              key={card._key}
              value={card.value}
              label={card.label}
              position={card.position}
              theme={card.theme}
              icon={card.icon && iconsMap[card.icon]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
